import type { StaffCatalogPlaceView } from "@lafayette/schemas";
import { useMemo } from "react";

import { AsyncState } from "../../ui/async-state";
import { CheckboxField, SelectField, TextField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import { SectionCard } from "../../ui/section-card";
import { SectionForm } from "../../ui/section-form";
import { StatusMessage } from "../../ui/status-message";
import { factList, fieldGrid, groupBox, groupLegend, hintText } from "../components/styles";
import { formatDateTime } from "../labels";
import {
  errorFieldsOf,
  type ItemSectionContext,
  useSectionState,
} from "../sections/section-support";
import { describePlaceOutcome, usePlaceCommand } from "./commands";
import { describePlaceError } from "./errors";
import { locationSourceLabels, locationSourceOptions } from "./labels";
import {
  hasAnyValue,
  type LocationFieldKey,
  type LocationValues,
  locationFieldLabels,
  locationFieldOrder,
  locationValuesFromItem,
  locationValuesToBody,
} from "./location-form";
import { PlaceBadge } from "./parts";
import { usePlaceView } from "./queries";
import { assessmentBadge } from "./state";
import { timeZoneOptions } from "./time-zones";

const ID_PREFIX = "location";
const RULE_ID = "location-source-rule";
const ZONE_LIST_ID = "location-timeZone-options";

/**
 * The location section: confirm the canonical geography of the item, or record that it is unknown. The
 * values are typed by the staff member from an independent source and a source is required. NOTHING here
 * is filled from a Google result: the form starts from the item's own saved values only.
 */
export function LocationSection({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  const { detail, capabilities } = ctx;
  const query = usePlaceView(detail.item.id, detail.item.version, capabilities.read);
  const description =
    "The coordinates, place details and time zone that Lafayette itself holds for this item.";

  if (query.isPending) {
    return (
      <SectionCard id="location" title="Location" description={description}>
        <AsyncState kind="loading" title="Loading the location" message="Reading the location." />
      </SectionCard>
    );
  }
  if (query.isError) {
    return (
      <SectionCard id="location" title="Location" description={description}>
        <AsyncState
          kind="error"
          title="The location could not be loaded"
          message={describePlaceError(query.error, { action: "read" }).message}
          onRetry={() => void query.refetch()}
        />
      </SectionCard>
    );
  }
  return <LocationForm ctx={ctx} view={query.data} description={description} />;
}

function LocationForm({
  ctx,
  view,
  description,
}: Readonly<{ ctx: ItemSectionContext; view: StaffCatalogPlaceView; description: string }>) {
  const { detail, readOnly, command: itemCommand } = ctx;
  const place = usePlaceCommand(detail.item.id);
  const server = useMemo(
    () => locationValuesFromItem(detail.item, view.location),
    [detail.item, view.location],
  );
  const section = useSectionState("location", server);
  const { values } = section;
  const status = view.location;

  async function submit() {
    const mapped = locationValuesToBody(values, detail.item.version);
    if (!mapped.ok) {
      section.reject(mapped.errors);
      return;
    }
    section.clearErrors();
    const outcome = await place.run("location", mapped.body, {
      lookupAvailable: view.lookupAvailable,
    });
    section.setMessage(describePlaceOutcome(outcome, "Location confirmed."));
    if (outcome.ok) {
      section.reset(
        locationValuesFromItem(outcome.result.detail.item, outcome.result.place.location),
      );
    }
  }

  const set = <K extends keyof LocationValues>(key: K, value: LocationValues[K]) =>
    section.setValues((current) => ({ ...current, [key]: value }));

  const errorFields = errorFieldsOf(
    section.errors,
    locationFieldOrder.map((key) => ({
      key,
      id: `${ID_PREFIX}-${key}`,
      label: locationFieldLabels[key],
    })),
  );
  const disabled = readOnly || place.pending || itemCommand.pending;
  const fieldId = (key: LocationFieldKey) => `${ID_PREFIX}-${key}`;
  const text = (
    key: "latitude" | "longitude" | "locality" | "region" | "countryCode" | "addressText",
    isOff: boolean,
    extra: { hint?: string; maxLength?: number; inputMode?: "decimal" } = {},
  ) => (
    <TextField
      id={fieldId(key)}
      label={locationFieldLabels[key]}
      value={values[key]}
      onChange={(next) => set(key, next)}
      error={section.errors[key]}
      disabled={disabled || isOff}
      {...extra}
    />
  );

  return (
    <SectionCard id="location" title="Location" description={description}>
      <dl className={factList}>
        <div>
          <dt>Coordinates</dt>
          <dd>
            <PlaceBadge badge={assessmentBadge(status.point)} />
          </dd>
        </div>
        <div>
          <dt>Locality, region, country</dt>
          <dd>
            <PlaceBadge badge={assessmentBadge(status.place)} />
          </dd>
        </div>
        <div>
          <dt>Time zone</dt>
          <dd>
            <PlaceBadge badge={assessmentBadge(status.timeZone)} />
          </dd>
        </div>
        <div>
          <dt>Source</dt>
          <dd>{status.source === null ? "None" : locationSourceLabels[status.source]}</dd>
        </div>
        {status.confirmedAt === null ? null : (
          <div>
            <dt>Confirmed</dt>
            <dd>{formatDateTime(status.confirmedAt)}</dd>
          </div>
        )}
      </dl>

      <Notice id={RULE_ID} tone="info" role="note">
        Enter coordinates from the venue, a site visit or a supplier. Do not copy them from Google.
      </Notice>
      <p className={hintText}>
        Saving the Details section changes these fields without a source and marks the location as
        unconfirmed. Use this form to confirm them.
      </p>

      <SectionForm
        label="Location"
        dirty={section.dirty}
        pending={place.pending || itemCommand.pending}
        readOnly={readOnly}
        message={section.message}
        errorFields={errorFields}
        errorRevision={section.revision}
        onSubmit={submit}
        onDiscard={section.discard}
        saveLabel="Confirm location"
      >
        {section.errors[""] === undefined ? null : (
          <StatusMessage message={{ tone: "error", text: section.errors[""] }} />
        )}

        <fieldset className={groupBox} aria-describedby={RULE_ID}>
          <legend className={groupLegend}>Coordinates</legend>
          <CheckboxField
            id={`${ID_PREFIX}-pointUnknown`}
            label="The coordinates are unknown"
            checked={values.pointUnknown}
            onChange={(checked) => set("pointUnknown", checked)}
            disabled={disabled}
          />
          <div className={fieldGrid}>
            {text("latitude", values.pointUnknown, {
              inputMode: "decimal",
              hint: "Between -90 and 90, for example 36.7213.",
            })}
            {text("longitude", values.pointUnknown, {
              inputMode: "decimal",
              hint: "Between -180 and 180, for example -4.4214.",
            })}
          </div>
        </fieldset>

        <fieldset className={groupBox} aria-describedby={RULE_ID}>
          <legend className={groupLegend}>Locality, region and country</legend>
          <CheckboxField
            id={`${ID_PREFIX}-placeUnknown`}
            label="The place details are unknown"
            checked={values.placeUnknown}
            onChange={(checked) => set("placeUnknown", checked)}
            disabled={disabled}
          />
          <div className={fieldGrid}>
            {text("locality", values.placeUnknown, { maxLength: 100 })}
            {text("region", values.placeUnknown, { maxLength: 100 })}
            {text("countryCode", values.placeUnknown, {
              maxLength: 2,
              hint: "Two letters, for example ES.",
            })}
            {text("addressText", values.placeUnknown, { maxLength: 300 })}
          </div>
        </fieldset>

        <fieldset className={groupBox} aria-describedby={RULE_ID}>
          <legend className={groupLegend}>Time zone</legend>
          <CheckboxField
            id={`${ID_PREFIX}-timeZoneUnknown`}
            label="The time zone is unknown"
            checked={values.timeZoneUnknown}
            onChange={(checked) => set("timeZoneUnknown", checked)}
            disabled={disabled}
          />
          <TextField
            id={fieldId("timeZone")}
            label={locationFieldLabels.timeZone}
            value={values.timeZone}
            onChange={(next) => set("timeZone", next)}
            maxLength={64}
            list={ZONE_LIST_ID}
            error={section.errors.timeZone}
            disabled={disabled || values.timeZoneUnknown}
            hint={
              status.suggestedTimeZone === null
                ? "An IANA name, for example Europe/Madrid. Start typing to see the names."
                : `Lafayette's own city table suggests ${status.suggestedTimeZone} for this locality. Check it before you type it.`
            }
          />
          <datalist id={ZONE_LIST_ID}>
            {timeZoneOptions(values.timeZone, status.suggestedTimeZone).map((option) => (
              <option key={option.value} value={option.value} label={option.label} />
            ))}
          </datalist>
        </fieldset>

        {hasAnyValue(values) ? (
          <SelectField
            id={fieldId("source")}
            label={locationFieldLabels.source}
            value={values.source}
            onChange={(next) => set("source", next as LocationValues["source"])}
            options={locationSourceOptions}
            placeholder="Choose a source"
            error={section.errors.source}
            required
            disabled={disabled}
            hint="Google is not a source. Use the venue, a site visit, a supplier, a licensed dataset or an import file."
          />
        ) : (
          <p className={hintText}>
            Every group is unknown, so no source is needed. This records that nobody knows the
            location yet.
          </p>
        )}
      </SectionForm>
    </SectionCard>
  );
}
